/**
 * Location Form Component
 * Form for creating and editing locations, with collapsible metadata section.
 */

import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import {
  createLocation,
  updateLocation,
} from "../../services/locations.service";
import {
  getOrganization,
  getOrganizationMetadata,
  findFacilityIdConflict,
  facilityIdConflictMessage,
} from "../../services/organizations.service";
import {
  USE_ORG_DEFAULT,
  applyLocationPromo,
  getLocationPromoContext,
} from "../../services/locationPromo.service";
import { useAuth } from "../../contexts/AuthContext";
import LocationPromoField from "./LocationPromoField";
import { geocodeAddress } from "../../services/geocoding.service";
import type { Location } from "../../types/organization.types";
import type { UUID } from "../../types/common.types";
import { US_STATES } from "../../types/event.types";
import Button from "../common/Button";
import Input from "../common/Input";
import MetadataForm from "./MetadataForm";

// Validation schema
const locationSchema = z.object({
  name: z.string().min(1, "Location name is required"),
  title: z.string().optional(),
  address_line1: z.string().min(1, "Address is required"),
  address_line2: z.string().optional(),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  postal_code: z.string().min(1, "Postal code is required"),
  country: z.string().optional(),
  phone_number: z.string().optional(),
  affiliate_id: z.string().optional(),
  facility_id: z.string().optional(),
});

type LocationFormData = z.infer<typeof locationSchema>;

interface LocationFormProps {
  organizationId: UUID;
  location?: Location;
  onSuccess?: (location: Location) => void;
  onCancel?: () => void;
}

const LocationForm: React.FC<LocationFormProps> = ({
  organizationId,
  location,
  onSuccess,
  onCancel,
}) => {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [metadataExpanded, setMetadataExpanded] = useState(false);
  const [locationMetadata, setLocationMetadata] = useState<
    Record<string, unknown>
  >((location?.metadata as Record<string, unknown>) || {});
  const [orgMetadata, setOrgMetadata] = useState<Record<string, unknown>>({});
  const { user } = useAuth();

  // The location's promo code lives on its location CTA, not on the location row.
  const { data: organization } = useQuery({
    queryKey: ["organization", organizationId],
    queryFn: () => getOrganization(organizationId),
    enabled: !!organizationId,
  });
  const { data: promoContext, isLoading: promoContextLoading } = useQuery({
    queryKey: ["location-promo-context", organizationId, location?.id ?? null],
    queryFn: () => getLocationPromoContext(organizationId, location?.id),
    enabled: !!organizationId,
  });
  const [promoChoice, setPromoChoice] = useState<string>(USE_ORG_DEFAULT);
  useEffect(() => {
    if (promoContext) {
      setPromoChoice(promoContext.currentCode || USE_ORG_DEFAULT);
    }
  }, [promoContext]);
  const orgDefaultCode = (organization?.promo_code ?? "").trim();

  // Fetch org-level metadata for inheritance
  useEffect(() => {
    getOrganizationMetadata(organizationId)
      .then(setOrgMetadata)
      .catch(() => {
        // Gracefully handle — no inherited metadata shown
      });
  }, [organizationId]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LocationFormData>({
    resolver: zodResolver(locationSchema),
    defaultValues: location
      ? {
          name: location.name || "",
          title: location.title || "",
          address_line1: location.address_line1 || "",
          address_line2: location.address_line2 || "",
          city: location.city || "",
          state: location.state || "",
          postal_code: location.postal_code || "",
          country: location.country || "USA",
          phone_number: location.phone_number || location.phone || "",
          affiliate_id: location.affiliate_id || "",
          facility_id: location.facility_id || "",
        }
      : {
          country: "USA",
        },
  });

  const onSubmit = async (data: LocationFormData) => {
    setIsSubmitting(true);
    setError("");
    setSuccessMessage("");

    try {
      let result: Location;

      // Facility ID (migration 996): checked only when it is introduced or changed, so an existing duplicate keeps
      // saving its other fields. This organization's own record and locations may share it; another
      // organization's may not. The database trigger enforces the same rule.
      const newFacility = (data.facility_id ?? "").trim();
      if (newFacility && newFacility !== (location?.facility_id ?? "").trim()) {
        const holder = await findFacilityIdConflict(
          newFacility,
          organizationId,
        );
        if (holder) {
          setError(facilityIdConflictMessage(newFacility, holder));
          setIsSubmitting(false);
          return;
        }
      }

      if (location?.id) {
        // Update existing location — only send form data + metadata, not organization_id
        const updateData = {
          ...data,
          metadata: locationMetadata,
        };
        result = await updateLocation(location.id, updateData);
        setSuccessMessage("Location updated successfully!");
      } else {
        // Create new location — include organization_id
        const createData = {
          ...data,
          organization_id: organizationId,
          is_archived: false,
          metadata: locationMetadata,
        };
        result = await createLocation(
          createData as Omit<Location, "id" | "created_at" | "updated_at">,
        );
        setSuccessMessage("Location created successfully!");
      }

      // Promo code: saved through the organization-CTA service after the location itself saved.
      let promoNote = "";
      if (organization && promoContext && user?.id) {
        try {
          const r = await applyLocationPromo({
            organization,
            locationId: result.id,
            location: {
              affiliate_id: data.affiliate_id,
              facility_id: data.facility_id,
            },
            choice: promoChoice,
            context: promoContext,
            userId: user.id,
          });
          if (
            r.kind === "created" ||
            r.kind === "updated" ||
            r.kind === "cleared"
          ) {
            promoNote =
              r.kind === "cleared"
                ? " Promo code now follows the organization."
                : " Promo code saved.";
            if (r.kind !== "created" && r.regenerateFailed) {
              promoNote +=
                " The link and QR image still carry the old code; press Regenerate URL & QR on the Calls to Action tab.";
            }
          }
        } catch (promoErr) {
          setError(
            `Location saved, but the promo code was not: ${
              promoErr instanceof Error ? promoErr.message : String(promoErr)
            }`,
          );
          setIsSubmitting(false);
          return;
        }
        queryClient.invalidateQueries({ queryKey: ["organizationCTAs"] });
        queryClient.invalidateQueries({
          queryKey: ["organization-location-promos", organizationId],
        });
        queryClient.invalidateQueries({ queryKey: ["location-promo-context"] });
      }
      if (promoNote) {
        setSuccessMessage((m) => m + promoNote);
      }

      // Invalidate locations query so the list refreshes
      queryClient.invalidateQueries({
        queryKey: ["locations", organizationId],
      });
      queryClient.invalidateQueries({
        queryKey: ["organization-stats", organizationId],
      });

      // Geocode the address in the background (fire-and-forget)
      if (data.address_line1 && data.city && data.state) {
        geocodeAddress(
          {
            address_line1: data.address_line1,
            city: data.city,
            state: data.state,
            postal_code: data.postal_code,
            country: data.country || "USA",
          },
          {
            updateRecord: { table: "locations", id: result.id },
          },
        ).catch(() => {
          // Geocoding failure is non-critical
        });
      }

      // Call onSuccess callback after a short delay to show the message
      setTimeout(() => {
        onSuccess?.(result);
      }, 1000);
    } catch (err) {
      // Supabase returns a plain PostgrestError object (not an Error instance): show its message, e.g. the
      // database's facility-ID refusal, instead of a generic failure.
      const message =
        err instanceof Error
          ? err.message
          : typeof (err as { message?: unknown })?.message === "string"
            ? (err as { message: string }).message
            : "";
      setError(message || "Failed to save location");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-800 text-sm">{successMessage}</p>
        </div>
      )}

      <Input
        {...register("name")}
        label="Location Name"
        placeholder="Enter location name"
        error={errors.name?.message}
        required
        fullWidth
      />

      <Input
        {...register("title")}
        label="Title"
        placeholder="e.g., Main Office, Warehouse, etc."
        error={errors.title?.message}
        fullWidth
      />

      <div className="grid grid-cols-2 gap-4">
        <Input
          {...register("affiliate_id")}
          label="Affiliate ID"
          placeholder="Affiliate ID (optional)"
          error={errors.affiliate_id?.message}
          fullWidth
        />

        <Input
          {...register("facility_id")}
          label="Facility ID"
          placeholder="Facility ID (optional)"
          error={errors.facility_id?.message}
          fullWidth
        />
      </div>

      <LocationPromoField
        organizationId={organizationId}
        locationId={location?.id}
        orgDefaultCode={orgDefaultCode}
        context={promoContext}
        contextLoading={promoContextLoading}
        value={promoChoice}
        onChange={setPromoChoice}
      />

      <Input
        {...register("address_line1")}
        label="Address Line 1"
        placeholder="Street address"
        error={errors.address_line1?.message}
        required
        fullWidth
      />

      <Input
        {...register("address_line2")}
        label="Address Line 2"
        placeholder="Apartment, suite, etc. (optional)"
        error={errors.address_line2?.message}
        fullWidth
      />

      <div className="grid grid-cols-2 gap-4">
        <Input
          {...register("city")}
          label="City"
          placeholder="Enter city"
          error={errors.city?.message}
          required
          fullWidth
        />

        <div className="w-full">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            State <span className="text-red-500">*</span>
          </label>
          <select
            {...register("state")}
            className={`w-full px-3 py-2 bg-white text-gray-900 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.state ? "border-red-500" : "border-gray-300"
            }`}
          >
            <option value="">Select state</option>
            {US_STATES.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name}
              </option>
            ))}
          </select>
          {errors.state && (
            <p className="mt-1 text-xs text-red-500">{errors.state.message}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Input
          {...register("postal_code")}
          label="Postal Code"
          placeholder="Enter postal code"
          error={errors.postal_code?.message}
          required
          fullWidth
        />

        <Input
          {...register("country")}
          label="Country"
          placeholder="Country"
          error={errors.country?.message}
          fullWidth
        />
      </div>

      <Input
        {...register("phone_number")}
        type="tel"
        label="Phone Number"
        placeholder="(555) 123-4567"
        error={errors.phone_number?.message}
        fullWidth
      />

      {/* Collapsible Metadata Section */}
      <div className="border border-gray-200 rounded-lg overflow-hidden mt-6">
        <button
          type="button"
          onClick={() => setMetadataExpanded(!metadataExpanded)}
          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center space-x-2">
            {metadataExpanded ? (
              <ChevronDownIcon className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronRightIcon className="h-4 w-4 text-gray-500" />
            )}
            <span className="text-sm font-semibold text-gray-900">
              Metadata
            </span>
          </div>
          <span className="text-xs text-gray-500">
            {Object.keys(locationMetadata).length} field(s) overridden
          </span>
        </button>

        {metadataExpanded && (
          <div className="p-4">
            <p className="text-xs text-gray-500 mb-3">
              Fields inherit from the organization unless overridden here. Use
              "Reset to org default" to revert individual fields.
            </p>
            <MetadataForm
              metadata={locationMetadata}
              inheritedMetadata={orgMetadata}
              onChange={setLocationMetadata}
            />
          </div>
        )}
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          {isSubmitting
            ? "Saving..."
            : location
              ? "Update Location"
              : "Create Location"}
        </Button>
      </div>
    </form>
  );
};

export default LocationForm;
